import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, DataGrid, Spinner } from '@ibsys/kit';
import type { DataGridLeafColumn, DataGridSelectedCell } from '@ibsys/kit';
import { QualityHeatmapResponse, type HeatmapCell, type HeatmapMode } from '@/entities/dashboards';
import { EmptyState } from '@/shared/ui';
import { fmt, heatmapClass } from './utils';

type Props = {
  data: QualityHeatmapResponse | undefined;
  isLoading: boolean;
  mode: HeatmapMode;
  drill: { consultant: string; period: string } | null;
  onSelectCell: (consultant: string, period: string) => void;
};

/** Строка DataGrid: консультант + его баллы по периодам и доли этих баллов от максимумов своих оценок */
type HeatmapRow = {
  consultant: string;
  periodScores: Record<string, number | null>;
  /**
   * Средняя доля балла от максимума своей оценки по периодам: по ней ячейка окрашивается в зону.
   * Лежит рядом со значением, а не вместо него: значение ячейки остаётся числом (сортировка,
   * фильтры, форматирование), а класс окраски берёт долю из строки.
   */
  periodShares: Record<string, number | null>;
};

/**
 * Карточка с тепловой картой «консультант × период».
 * Клик по ячейке периода открывает drill-down в «Деталях выбранного периода».
 * При смене набора периодов (новый диапазон дат) компонент пересоздаётся
 * через key, чтобы useDataGridColumnsState сбросил состояние колонок.
 */
export const HeatmapCard: FC<Props> = ({ data, isLoading, mode, drill, onSelectCell }) => {
  const { t } = useTranslation('qualityDashboard');

  const consultants = data?.consultants ?? [];
  const periods = data?.periods ?? [];

  const periodsKey = periods.join(',');

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <CardTitle>{t('heatmap.title')}</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
            {`${t('heatmap.modeLabel')}: ${mode === 'month' ? t('heatmap.modeMonth') : t('heatmap.modeWeek')}`}
          </span>
          <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
            {drill
              ? t('heatmap.drillActive', { consultant: drill.consultant, period: drill.period })
              : t('heatmap.drillInactive')}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : consultants.length === 0 || periods.length === 0 ? (
          <EmptyState />
        ) : (
          <HeatmapTable key={periodsKey} data={data!} drill={drill} onSelectCell={onSelectCell} />
        )}
      </CardContent>
    </Card>
  );
};

type HeatmapTableProps = {
  data: QualityHeatmapResponse;
  drill: { consultant: string; period: string } | null;
  onSelectCell: (consultant: string, period: string) => void;
};

const HeatmapTable: FC<HeatmapTableProps> = ({ data, drill, onSelectCell }) => {
  const { t } = useTranslation('qualityDashboard');

  const { consultants, periods } = data;

  const rows = useMemo((): HeatmapRow[] => {
    const cellMap: Record<string, Record<string, HeatmapCell>> = {};
    for (const cell of data.cells) {
      if (!cellMap[cell.consultant]) cellMap[cell.consultant] = {};
      cellMap[cell.consultant][cell.period] = cell;
    }
    return consultants.map((c) => ({
      consultant: c,
      periodScores: Object.fromEntries(periods.map((p) => [p, cellMap[c]?.[p]?.avg ?? null])),
      periodShares: Object.fromEntries(periods.map((p) => [p, cellMap[c]?.[p]?.avgShare ?? null])),
    }));
  }, [data, consultants, periods]);

  const columns = useMemo(
    (): DataGridLeafColumn<HeatmapRow>[] => [
      {
        id: 'consultant',
        header: t('heatmap.consultantColumn'),
        accessor: 'consultant',
        size: 180,
      },
      ...periods.map(
        (p): DataGridLeafColumn<HeatmapRow> => ({
          id: p,
          header: p,
          accessor: (row) => row.periodScores[p] ?? null,
          size: 100,
          align: 'center',
          className: 'font-bold',
          cellClassName: ({ row }) => heatmapClass(row.periodShares[p]),
          cell: ({ value }) => fmt(value as number | null),
        }),
      ),
    ],
    [t, periods],
  );

  // Маппинг выделенной ячейки DataGrid → состояние drill
  const selectedCell: DataGridSelectedCell | null = drill ? { rowId: drill.consultant, columnId: drill.period } : null;

  const handleCellChange = (cell: DataGridSelectedCell | null) => {
    // Игнорируем клик по колонке консультанта — только периоды кликабельны
    if (cell && cell.columnId !== 'consultant') {
      onSelectCell(cell.rowId, cell.columnId);
    }
  };

  return (
    <DataGrid
      columns={columns}
      dataSource={{ mode: 'client', data: rows }}
      getRowId={(r) => r.consultant}
      enableColumnResizing
      enableCellSelection
      selectedCell={selectedCell}
      onSelectedCellChange={handleCellChange}
      height={512}
    />
  );
};
